import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import { nonNullable, sum } from 'lib/array/arrayUtils'
import { getPointsCalculationsByKeys } from 'loyalty/selectors/pointsCalculation'
import submitLoyaltyPointsEarnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsEarnCalculationRequest'

const INITIAL_LUX_LOYALTY_POINTS_EARN_CALCULATION_STATE: App.StatefulData<App.LoyaltyPointsEarnCalculation, boolean> = {
  initial: true,
}

interface TotalPointsCalculation {
  totalPointsCalculation: App.StatefulData<Omit<App.LoyaltyPointsEarnCalculation, 'request'>, boolean>
  /** @dev primarily used for debugging individual points calculations */
  pointsCalculations: Array<App.StatefulData<App.LoyaltyPointsEarnCalculation> | undefined>
}

interface Options {
  skipAll?: boolean
}

function useLoyaltyPointsEarnCalculator(
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined,
  options?: Options,
): TotalPointsCalculation {
  const dispatch = useAppDispatch()
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const { skipAll } = options ?? {}

  const requestsToCalculate = useMemo(() => {
    if (skipAll) return []

    const requests = Array.isArray(calculationRequests) ? calculationRequests : [calculationRequests]
    return nonNullable(requests)
  // Please keep the customerId and currency as dependencies (see related action)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculationRequests, skipAll, customerId, currency])

  useEffect(() => {
    requestsToCalculate.forEach((calculationRequest) => {
      dispatch(submitLoyaltyPointsEarnCalculationRequest(calculationRequest))
    })
  }, [dispatch, requestsToCalculate])

  const pointsKeys = useMemo(() => requestsToCalculate.map((calculationRequest) => buildPointsKey(calculationRequest, customerId)), [requestsToCalculate, customerId])
  const pointsCalculations = useAppSelector(state => getPointsCalculationsByKeys(state, pointsKeys))

  return useMemo((): TotalPointsCalculation => {
    const isInitial = !!pointsCalculations.every(pointsCalculation => !!pointsCalculation?.initial)
    const anyError = !!pointsCalculations.some(pointsCalculation => !!pointsCalculation?.error)
    const anyFetching = !!pointsCalculations.some(pointsCalculation => !!pointsCalculation?.fetching)

    if (isInitial) {
      return {
        totalPointsCalculation: INITIAL_LUX_LOYALTY_POINTS_EARN_CALCULATION_STATE,
        pointsCalculations,
      }
    }

    if (anyFetching) {
      return {
        totalPointsCalculation: {
          fetching: true,
        },
        pointsCalculations,
      }
    }

    if (anyError) {
      return {
        totalPointsCalculation: {
          error: true,
          fetching: false,
        },
        pointsCalculations,
      }
    }

    const basePoints = sum(pointsCalculations, calculation => calculation?.data?.basePoints ?? 0)
    const bonusPoints = sum(pointsCalculations, calculation => calculation?.data?.bonusPoints ?? 0)
    const allAppliedRuleIds = pointsCalculations.flatMap(calculation => calculation?.data?.appliedRuleIds ?? [])

    return {
      totalPointsCalculation: {
        fetching: false,
        data: {
          points: basePoints + bonusPoints,
          basePoints,
          bonusPoints,
          // If any calculation has rounding disabled, then we should use that for the total
          roundingEnabled: !pointsCalculations.some(calculation => !calculation?.data?.roundingEnabled),
          roundedPointsEarnRatio: sum(pointsCalculations, calculation => calculation?.data?.roundedPointsEarnRatio ?? 0),
          statusCredits: sum(pointsCalculations, calculation => calculation?.data?.statusCredits ?? 0),
          appliedRuleIds: allAppliedRuleIds,
        },
      },
      pointsCalculations,
    }
  }, [pointsCalculations])
}

export default useLoyaltyPointsEarnCalculator
